import ReactDOM from 'react-dom';

import { CssBaseline } from '@mui/material';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';

import App from './App';
import AppStateProvider, { useAppState } from './state';
import { BrowserRouter as Router, Redirect, Switch } from 'react-router-dom';
import ErrorDialog from './components/ErrorDialog/ErrorDialog';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import theme from './theme';
import './types';
import { ChatProvider } from './components/ChatProvider';
import { ParticipantProvider } from './components/ParticipantProvider';
import { VideoProvider } from './components/VideoProvider';
import useConnectionOptions from './utils/useConnectionOptions/useConnectionOptions';
import UnsupportedBrowserWarning from './components/UnsupportedBrowserWarning/UnsupportedBrowserWarning';
import VoiceApp from './VoiceApp';
import { VoiceProvider } from './components/VoiceProvider';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const VideoApp = () => {
  const { error, setError } = useAppState();
  const connectionOptions = useConnectionOptions();

  return (
    <VideoProvider options={connectionOptions} onError={setError}>
      <ErrorDialog dismissError={() => setError(null)} error={error} />
      <ParticipantProvider>
        <ChatProvider>
          <App />
        </ChatProvider>
      </ParticipantProvider>
    </VideoProvider>
  );
};

const VoiceAppContainer = () => {
  const { error, setError } = useAppState();
  return (
    <UnsupportedBrowserWarning>
      <VoiceProvider>
        <ErrorDialog dismissError={() => setError(null)} error={error} />
        <VoiceApp />
      </VoiceProvider>
    </UnsupportedBrowserWarning>
  );
};

export const ReactApp = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UnsupportedBrowserWarning>
        <Router>
          <AppStateProvider>
            <Switch>
              <PrivateRoute exact path="/video">
                <VideoApp />
              </PrivateRoute>
              <PrivateRoute path="/voice">
                <VoiceAppContainer />
              </PrivateRoute>
              <Redirect to={`/video/${window.location.search}`} />
            </Switch>
          </AppStateProvider>
        </Router>
      </UnsupportedBrowserWarning>
    </ThemeProvider>
  </StyledEngineProvider>
);

ReactDOM.render(<ReactApp />, document.getElementById('root'));
